import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn';
import { springQuick, tapScale } from '../tokens';

export type TabDef<T extends string> = {
  id: T;
  label: string;
  icon: LucideIcon;
};

type TabBarProps<T extends string> = {
  tabs: TabDef<T>[];
  active: T;
  onChange: (id: T) => void;
  className?: string;
};

/**
 * Bottom tab bar — pinned to the viewport edge with safe-area padding.
 * Icons only switch color (accent vs tertiary); no badges, no separators.
 * The parent owns swipe gestures between tabs (this just renders the chrome).
 */
export function TabBar<T extends string>({
  tabs,
  active,
  onChange,
  className,
}: TabBarProps<T>) {
  return (
    <nav
      aria-label="Primary"
      style={{
        willChange: 'transform',
        background: 'rgba(var(--surface) / 0.72)',
        backdropFilter: 'blur(30px) saturate(180%)',
        WebkitBackdropFilter: 'blur(30px) saturate(180%)',
        borderTop: '1px solid rgba(0, 0, 0, 0.04)',
      }}
      className={cn(
        'fixed inset-x-0 bottom-0 z-30',
        'pb-[env(safe-area-inset-bottom)]',
        className,
      )}
    >
      <div className="mx-auto max-w-content flex items-stretch">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.id === active;
          return (
            <motion.button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              whileTap={tapScale}
              transition={springQuick}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'relative flex-1 inline-flex flex-col items-center justify-center gap-1',
                'h-[56px] min-h-tap',
                isActive ? 'text-accent' : 'text-text-tertiary',
              )}
            >
              <Icon size={24} strokeWidth={1.5} />
              <span className="text-[11px] font-medium">{tab.label}</span>
              {isActive ? (
                <motion.span
                  layoutId="tabbar-dot"
                  className="absolute bottom-[6px] left-1/2 -translate-x-1/2 w-1 h-1 rounded-pill bg-accent"
                  transition={springQuick}
                  aria-hidden
                />
              ) : null}
            </motion.button>
          );
        })}
      </div>
    </nav>
  );
}
